/**
 * frontend/src/lib/rates.js — LIVE reward amounts (GET /api/rewards/rates).
 *
 * Every screen that promises an amount reads it from here: floor(base × era × tonight's
 * governor multiplier), refreshed every 5 minutes. Until the first response arrives the
 * number is simply left out ("SOUND") — never a hard-coded base rate.
 */

import { useEffect, useSyncExternalStore } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const REFRESH_MS = 5 * 60 * 1000;

let state = { loaded: false, rates: {}, drop: null };
let fetchedAt = 0;
let inflight = null;
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const snapshot = () => state;

export function loadRates(force = false) {
  if (!force && (inflight || Date.now() - fetchedAt < REFRESH_MS)) return inflight;
  inflight = fetch(`${API_BASE}/api/rewards/rates`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`rates ${r.status}`))))
    .then((d) => { state = { loaded: true, rates: d.rates || {}, drop: d.drop || null }; fetchedAt = Date.now(); emit(); })
    .catch(() => { /* keep the last known amounts; copy falls back to no number */ })
    .finally(() => { inflight = null; });
  return inflight;
}

/** useRates() → { loaded, rates: { action: amount }, drop: { multiplier, creator_content } } */
export function useRates() {
  const s = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => {
    loadRates();
    const t = setInterval(() => loadRates(), REFRESH_MS);
    return () => clearInterval(t);
  }, []);
  return s;
}

/** "580 SOUND" when known, otherwise "SOUND". */
export function sound(r, action) {
  const n = r?.rates?.[action];
  return Number.isFinite(n) ? `${n.toLocaleString()} SOUND` : 'SOUND';
}

/** "+580" when known, otherwise "+". */
export function plus(r, action) {
  const n = r?.rates?.[action];
  return Number.isFinite(n) ? `+${n.toLocaleString()}` : '+';
}

/** "696 SOUND" per post during a live Drop (creator_content × Drop multiplier), otherwise "SOUND". */
export function dropSound(r) {
  const n = r?.drop?.creator_content;
  return Number.isFinite(n) ? `${n.toLocaleString()} SOUND` : 'SOUND';
}
